// /opt/isp_erp/frontend/src/pages/warehouse/Pendientes.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Tooltip
} from '@mui/material';
import { 
  Warning, 
  CheckCircle, 
  Receipt, 
  Refresh,
  Schedule,
  ReportProblem
} from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';

const Pendientes = () => {
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recepcionOpen, setRecepcionOpen] = useState(false);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [recepcionData, setRecepcionData] = useState({
    observaciones: '',
    necesita_rma: false,
    diagnostico_rma: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    cargarPendientes();
  }, []);

  const cargarPendientes = async () => {
    setLoading(true);
    try {
      const response = await warehouseAPI.getPendientesRetiro();
      setPendientes(response.data || []);
    } catch (error) {
      console.error('Error cargando pendientes:', error);
      setError('Error al cargar equipos pendientes');
    } finally {
      setLoading(false);
    }
  };

  const abrirRecepcion = (equipo) => {
    setSelectedEquipo(equipo);
    setRecepcionData({
      observaciones: '',
      necesita_rma: false,
      diagnostico_rma: ''
    });
    setRecepcionOpen(true);
  };

  const registrarRecepcion = async () => {
    setSubmitting(true);
    try {
      await warehouseAPI.recepcionEquipo({
        sn: selectedEquipo.sn,
        observaciones: recepcionData.observaciones,
        necesita_rma: recepcionData.necesita_rma,
        diagnostico_rma: recepcionData.diagnostico_rma
      });
      
      setSuccess(true);
      setRecepcionOpen(false);
      cargarPendientes();
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al registrar recepción');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const getDiasColor = (dias) => {
    if (dias > 3) return 'error';
    if (dias > 1) return 'warning';
    return 'info';
  };

  const getDiasTexto = (dias) => {
    if (dias === 0) return 'Hoy';
    if (dias === 1) return '1 día';
    return `${dias} días`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Equipos Pendientes de Devolución
        </Typography>
        <Button
          variant="outlined"
          onClick={cargarPendientes}
          startIcon={<Refresh />}
          disabled={loading}
        >
          Actualizar
        </Button>
      </Box>

      {/* Alerta de equipos con retraso */}
      {pendientes.some(p => p.dias_pendiente > 2) && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<ReportProblem />}>
          Hay equipos con más de 2 días de retraso en la devolución.
          Por favor, contacte a los técnicos responsables.
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>SN</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Técnico Responsable</TableCell>
                  <TableCell>Fecha de Retiro</TableCell>
                  <TableCell>Días Pendiente</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Cargando equipos pendientes...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pendientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                      <Typography variant="body1" color="success.main">
                        No hay equipos pendientes de devolución
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Todos los equipos retirados han sido devueltos a bodega
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendientes.map((item) => (
                    <TableRow key={item.equipo?.id} hover>
                      <TableCell>
                        <strong>{item.equipo?.sn}</strong>
                      </TableCell>
                      <TableCell>{item.equipo?.modelo}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title="Técnico asignado">
                            <Schedule fontSize="small" color="action" />
                          </Tooltip>
                          {item.tecnico?.nombre}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(item.fecha_retiro)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getDiasTexto(item.dias_pendiente)}
                          color={getDiasColor(item.dias_pendiente)}
                          size="small"
                          icon={item.dias_pendiente > 2 ? <Warning /> : undefined}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.dias_pendiente > 2 ? "RETRASADO" : "PENDIENTE"}
                          color={item.dias_pendiente > 2 ? "error" : "warning"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Receipt />}
                          onClick={() => abrirRecepcion(item.equipo)}
                          color={item.dias_pendiente > 2 ? "error" : "primary"}
                        >
                          Recibir en Bodega
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog de Recepción */}
      <Dialog 
        open={recepcionOpen} 
        onClose={() => !submitting && setRecepcionOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Recepción de Equipo en Bodega
          {selectedEquipo && (
            <Typography variant="body2" color="text.secondary">
              SN: {selectedEquipo.sn} - {selectedEquipo.modelo}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Observaciones"
              value={recepcionData.observaciones}
              onChange={(e) => setRecepcionData({ ...recepcionData, observaciones: e.target.value })}
              placeholder="Ingrese observaciones sobre el estado del equipo..."
              sx={{ mb: 2 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={recepcionData.necesita_rma}
                  onChange={(e) => setRecepcionData({ ...recepcionData, necesita_rma: e.target.checked })}
                />
              }
              label="¿El equipo necesita RMA (dañado)?"
            />
            
            {recepcionData.necesita_rma && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Diagnóstico RMA"
                value={recepcionData.diagnostico_rma}
                onChange={(e) => setRecepcionData({ ...recepcionData, diagnostico_rma: e.target.value })}
                placeholder="Describa el daño o falla del equipo..."
                sx={{ mt: 2 }}
                required
                error={recepcionData.necesita_rma && !recepcionData.diagnostico_rma}
                helperText={recepcionData.necesita_rma && !recepcionData.diagnostico_rma ? "El diagnóstico es requerido" : ""}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRecepcionOpen(false)} 
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button 
            onClick={registrarRecepcion} 
            variant="contained" 
            color="primary"
            disabled={submitting || (recepcionData.necesita_rma && !recepcionData.diagnostico_rma)}
          >
            {submitting ? <CircularProgress size={24} /> : 'Registrar Recepción'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de éxito */}
      {success && (
        <Alert 
          severity="success" 
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setSuccess(false)}
        >
          Equipo recibido en bodega exitosamente
        </Alert>
      )}

      {/* Snackbar de error */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default Pendientes;
